import type { Component } from "solid-js";
import { useI18n } from "@/api/i18n";

/**
 * Placeholder di `ProfileInfo` per la finestra di fetch del profilo dentro le
 * sheet (ricerca attiva e talent pool).
 *
 * **Perché uno scheletro e non uno spinner.** Lo stato precedente era un
 * `<Loading />` centrato in un box `min-h-[30vh]`, rimpiazzato di colpo dal
 * profilo intero: un salto di altezza giocato *mentre* la sheet sta ancora
 * animando la propria entrata, cioè due movimenti sulla stessa superficie.
 * Riservando la geometria al primo frame, l'unica cosa che si muove è la sheet.
 * (Lo spinner era anche `h-screen` di default, quindi il salto partiva da
 * un'altezza di viewport intera.)
 *
 * **Perché qui e non dentro le sheet.** La sua unica ragione d'essere è imitare
 * la prima schermata di `ProfileInfo`: deve cambiare insieme a quel componente,
 * e non essere duplicato nei due chiamanti.
 *
 * Le proporzioni ricalcano i blocchi reali — nome, ruolo, badge, skill, lingue,
 * barra del punteggio, bio. Non devono essere esatte (la bio ha lunghezza
 * variabile), solo abbastanza vicine da togliere il salto.
 *
 * `skeleton-shimmer` (`src/index.css`) e non `LoadingSkeleton`: usa i token del
 * tema invece di un grigio cablato e porta già dentro il ramo
 * `prefers-reduced-motion`.
 */
export const ProfileInfoSkeleton: Component = () => {
  const { t } = useI18n();

  return (
    <div
      role="status"
      aria-label={t("common.loading")}
      class="max-w-full w-full p-2 lg:mx-auto"
    >
      {/* Stesse classi di spaziatura del contenitore reale: è la spaziatura, non
          le singole altezze, a determinare quanto resta di assestamento. */}
      <div aria-hidden="true" class="w-full flex-1 space-y-6">
        <div class="space-y-4">
          <div class="skeleton-shimmer h-7 w-56 max-w-full rounded-md lg:h-8" />
          <div class="skeleton-shimmer h-5 w-40 max-w-full rounded-md" />

          {/* Riga dei badge: tariffa, disponibilità, modalità di lavoro. */}
          <div class="flex flex-wrap items-center gap-2">
            <div class="skeleton-shimmer h-5 w-24 max-w-full rounded-full" />
            <div class="skeleton-shimmer h-5 w-20 max-w-full rounded-full" />
            <div class="skeleton-shimmer h-5 w-28 max-w-full rounded-full" />
          </div>

          <div class="space-y-2">
            <div class="skeleton-shimmer h-4 w-16 max-w-full rounded-md" />
            <div class="flex flex-wrap items-center gap-2">
              <div class="skeleton-shimmer h-6 w-20 max-w-full rounded-full" />
              <div class="skeleton-shimmer h-6 w-24 max-w-full rounded-full" />
              <div class="skeleton-shimmer h-6 w-16 max-w-full rounded-full" />
              <div class="skeleton-shimmer h-6 w-28 max-w-full rounded-full" />
            </div>
          </div>

          <div class="space-y-2">
            <div class="skeleton-shimmer h-4 w-20 max-w-full rounded-md" />
            <div class="flex flex-wrap items-center gap-2">
              <div class="skeleton-shimmer h-6 w-24 max-w-full rounded-full" />
              <div class="skeleton-shimmer h-6 w-20 max-w-full rounded-full" />
            </div>
          </div>
        </div>

        {/* Barra del punteggio di match. */}
        <div class="skeleton-shimmer h-2 w-full rounded-full" />

        <hr class="my-4 border-gray-200" />

        <div class="space-y-2">
          <div class="skeleton-shimmer h-5 w-24 max-w-full rounded-md" />
          <div class="space-y-1">
            <div class="skeleton-shimmer h-4 w-full rounded-md" />
            <div class="skeleton-shimmer h-4 w-full rounded-md" />
            <div class="skeleton-shimmer h-4 w-2/3 rounded-md" />
          </div>
        </div>
      </div>
    </div>
  );
};
